import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { FaPaperclip, FaImage, FaCamera, FaVideo, FaDownload, FaTrash } from "react-icons/fa";
import "./chat.css";
import Webcam from "react-webcam";

/* ── stable counter so every message gets a unique local id ── */
let _cnt = 0;
const mkId = () => `m_${Date.now()}_${++_cnt}`;

const Chat = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [name] = useState(() => location.state?.name || "Anonymous");

  const [socket, setSocket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [message, setMessage] = useState("");
  const [showUsers, setShowUsers] = useState(false);

  const [showCamera, setShowCamera] = useState(false);
  const [flashActive, setFlashActive] = useState(false);
  const webcamRef = useRef(null);

  const [lightbox, setLightbox] = useState(null); // { src, sender, localId }
  const [openMenu, setOpenMenu] = useState(null); // localId of open menu

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  /* ── socket connect ── */
  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://whatsappbackend-sq5p.onrender.com";
    const s = io(BACKEND_URL, { transports: ["websocket", "polling"] });
    setSocket(s);
    return () => s.disconnect();
  }, []);

  /* ── socket events ── */
  useEffect(() => {
    if (!socket) return;
    if (name && name !== "Anonymous") socket.emit("join", name);

    const stamp = (msg) => ({ ...msg, _localId: mkId() });

    socket.on("message", (msg) => setMessages((p) => [...p, stamp(msg)]));
    socket.on("fileMessage", (msg) => setMessages((p) => [...p, stamp(msg)]));
    socket.on("users", setUsers);

    socket.on("deleteForEveryone", ({ socketMsgId }) =>
      setMessages((p) => p.filter((m) => m.socketMsgId !== socketMsgId))
    );

    return () => {
      socket.off("message");
      socket.off("fileMessage");
      socket.off("users");
      socket.off("deleteForEveryone");
    };
  }, [socket, name]);

  /* ── auto scroll ── */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /* ── close menu on click outside ── */
  useEffect(() => {
    const close = () => setOpenMenu(null);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  /* ── send text ── */
  const sendMessage = () => {
    if (!message.trim() || !socket) return;
    const socketMsgId = mkId();
    socket.emit("message", { user: name, text: message.trim(), socketMsgId });
    setMessage("");
  };

  /* ── send file ── */
  const sendFile = (e, type) => {
    const file = e.target.files[0];
    if (!file || !socket) return;
    const reader = new FileReader();
    reader.onload = () => {
      const socketMsgId = mkId();
      socket.emit("fileMessage", {
        user: name,
        fileName: file.name,
        fileData: reader.result,
        fileType: type,
        socketMsgId,
      });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  /* ── camera capture ── */
  const captureImage = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setFlashActive(true);
    setTimeout(() => setFlashActive(false), 420);
    const socketMsgId = mkId();
    socket.emit("fileMessage", {
      user: name,
      fileData: imageSrc,
      fileType: "camera",
      socketMsgId,
    });
    setTimeout(() => setShowCamera(false), 380);
  };

  /* ── DELETE FOR ME ── */
  const deleteForMe = useCallback((localId) => {
    setMessages((p) => p.filter((m) => m._localId !== localId));
    setOpenMenu(null);
    setLightbox((prev) => (prev?.localId === localId ? null : prev));
  }, []);

  /* ── DELETE FOR EVERYONE ── */
  const deleteForEveryone = useCallback(
    (localId, socketMsgId) => {
      if (socketMsgId) socket?.emit("deleteForEveryone", { socketMsgId });
      deleteForMe(localId);
    },
    [socket, deleteForMe]
  );

  /* ── download ── */
  const download = (src, name = "image.jpg") => {
    const a = document.createElement("a");
    a.href = src;
    a.download = name;
    a.click();
  };

  return (
    <div className="mainContainer">
      {showUsers && <div className="mobileOverlay" onClick={() => setShowUsers(false)} />}

      {/* SIDEBAR */}
      <div className={`usersSection ${showUsers ? "show" : ""}`}>
        <div className="usersHeader">
          <h3>Online Users</h3>
          <button className="closeButton" onClick={() => setShowUsers(false)}>✕</button>
        </div>
        <div className="userList">
          {users.filter((u) => u.id !== socket?.id).length === 0 ? (
            <p className="noUsers">No other users online</p>
          ) : (
            users.filter((u) => u.id !== socket?.id).map((u) => (
              <div key={u.id} className="userItem">
                <span className="userDot" />
                {u.name}
              </div>
            ))
          )}
        </div>
      </div>

      {/* CHAT */}
      <div className="chatSection">
        {/* Header */}
        <div className="chatHeader">
          <button className="menuButton" onClick={() => setShowUsers(true)}>☰</button>
          <div className="headerInfo">
            <h3>Group Chat</h3>
            <p className="onlineCount">{users.length} members</p>
          </div>
          <div className="headerActions">
            <button className="videoCallBtn" onClick={() => navigate("/video", { state: { name } })}>
              <FaVideo />
              <span>Video Call</span>
            </button>
            <span className="userDisplay">{name}</span>
          </div>
        </div>

        {/* Messages */}
        <div className="chatMessages">
          {messages.map((msg) => {
            const isOwn = msg.user === name;
            const isImg = msg.fileData && (msg.fileType === "image" || msg.fileType === "camera");
            const isFile = msg.fileData && msg.fileType === "file";

            return (
              <div key={msg._localId} className={`messageWrapper ${isOwn ? "ownWrapper" : ""}`}>
                <div className={`message ${isOwn ? "ownMessage" : "otherMessage"}`}>
                  {!isOwn && <span className="messageUser">{msg.user}</span>}

                  {msg.text != null && msg.text !== "" && <p className="msgText">{String(msg.text).trim()}</p>}

                  {/* Image / Camera */}
                  {isImg && (
                    <div
                      className="chatImageWrapper"
                      onClick={() => setLightbox({ src: msg.fileData, sender: msg.user, localId: msg._localId })}
                    >
                      <img src={msg.fileData} alt="shared" className="chatImage" />
                      {msg.fileType === "camera" && <span className="imgBadge">📷 Camera</span>}
                    </div>
                  )}

                  {/* File */}
                  {isFile && (
                    <a href={msg.fileData} download={msg.fileName} className="fileDownload">
                      <span>📄</span>
                      <span>{msg.fileName}</span>
                    </a>
                  )}

                  {/* Menu */}
                  <div className="msgMenuWrapper" onMouseDown={(e) => e.stopPropagation()}>
                    <button
                      className="msgMenuDot"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setOpenMenu((prev) => (prev === msg._localId ? null : msg._localId));
                      }}
                    >
                      ⋮
                    </button>

                    {openMenu === msg._localId && (
                      <div className="msgMenuDropdown" onMouseDown={(e) => e.stopPropagation()}>
                        {isImg && (
                          <button
                            className="menuItem"
                            onMouseDown={() => {
                              download(msg.fileData, msg.fileName || "image.jpg");
                              setOpenMenu(null);
                            }}
                          >
                            <FaDownload className="menuItemIcon" />
                            <span>Download</span>
                          </button>
                        )}

                        <button className="menuItem menuItemDanger" onMouseDown={() => deleteForMe(msg._localId)}>
                          <FaTrash className="menuItemIcon" />
                          <span>Delete for me</span>
                        </button>

                        {isOwn && (
                          <>
                            <div className="msgMenuDivider" />
                            <button
                              className="menuItem menuItemDanger"
                              onMouseDown={() => deleteForEveryone(msg._localId, msg.socketMsgId)}
                            >
                              <FaTrash className="menuItemIcon" />
                              <span>Delete for everyone</span>
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Input bar */}
        <div className="chatInputArea">
          <button className="inputIconBtn" title="Attach file" onClick={() => fileInputRef.current.click()}>
            <FaPaperclip />
          </button>
          <button className="inputIconBtn" title="Send image" onClick={() => imageInputRef.current.click()}>
            <FaImage />
          </button>
          <button className="inputIconBtn" title="Camera" onClick={() => setShowCamera(true)}>
            <FaCamera />
          </button>

          <input
            type="text"
            placeholder="Type a message…"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          />

          <button className="sendBtn" onClick={sendMessage} title="Send">
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>

          <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={(e) => sendFile(e, "file")} />
          <input type="file" accept="image/*" ref={imageInputRef} style={{ display: "none" }} onChange={(e) => sendFile(e, "image")} />
        </div>
      </div>

      {/* CAMERA MODAL */}
      {showCamera && (
        <div className="cameraModal">
          <div className="cameraBox">
            <div className="cameraHeader">
              <span className="cameraTitle">Take a Photo</span>
              <button className="cameraCloseBtn" onClick={() => setShowCamera(false)}>✕</button>
            </div>
            <div className="webcamFrame">
              <Webcam
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                className="webcamView"
                videoConstraints={{ facingMode: "user" }}
                mirrored
              />
              {flashActive && <div className="cameraFlash" />}
            </div>
            <div className="cameraControls">
              <button className="captureBtn" onClick={captureImage}>📸 Capture</button>
              <button className="cancelBtn" onClick={() => setShowCamera(false)}>✕ Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* LIGHTBOX */}
      {lightbox && (
        <div className="lightboxOverlay" onClick={() => setLightbox(null)}>
          <div className="lightboxContent" onClick={(e) => e.stopPropagation()}>
            <div className="lightboxBar">
              <span className="lightboxSender">{lightbox.sender}</span>
              <button className="lightboxClose" onClick={() => setLightbox(null)}>✕</button>
            </div>
            <img src={lightbox.src} alt="preview" className="lightboxImg" />
            <div className="lightboxFooter">
              <button className="lbBtn lbBtnDownload" onClick={() => download(lightbox.src)}>
                <FaDownload style={{ marginRight: 7 }} /> Download
              </button>
              <button className="lbBtn lbBtnDelete" onClick={() => deleteForMe(lightbox.localId)}>
                <FaTrash style={{ marginRight: 7 }} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;